import './styles/WatchlistAddButton.scss';

import { BookmarkAdd, BookmarkAdded } from '@mui/icons-material';
import { Button, Fab, Grid, MenuItem, Skeleton } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { CreateWatchlistItem } from '@thro-types/users';

import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { useWatchlistListQuery, useWatchlistMutation } from 'api/hooks/watchlist';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface WatchlistAddButtonProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'outlined' | 'contained' | 'text';
  menuButton?: boolean;
  gridCellMenuItem?: boolean;
  showInMenu?: boolean;
  eventButton?: boolean;
  categoryName:
    | 'ARTICLE'
    | 'BULLETIN'
    | 'EVENT'
    | 'FUNDING'
    | 'MODULE_SELECTION'
    | 'JOB_OFFER'
    | 'STUDENT_OFFER'
    | 'COMPANY';
  title: string;
  description: string;
  customLink?: string;
  date: Date | undefined; //Todo  remove undefined if in db module selection start Date cant be null
  /**
   * Should be used when `date: new Date()` because the date filter would otherwise never find an item with the combination of link and `new Date()`
   */
  withoutDateFilter?: boolean;
}

export const WatchlistAddButton = ({
  size,
  categoryName,
  title,
  description,
  variant,
  menuButton,
  gridCellMenuItem,
  showInMenu,
  eventButton,
  customLink,
  date,
  withoutDateFilter
}: WatchlistAddButtonProps) => {
  const { update } = useWatchlistMutation();
  const dispatch = useDispatch();
  const handleAdd = async ({
    categoryName,
    link,
    title,
    description,
    date
  }: CreateWatchlistItem) => {
    try {
      await update.mutateAsync({ categoryName, link, title, description, date });
      dispatch(AlertActions.success(t(translations.pages.home.watchlist.addSuccess())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.home.watchlist.addError())));
    }
  };
  const location = useLocation();

  let link = location.pathname;
  if (customLink) {
    link = customLink;
  }

  //Used to check if item is already added to watchlist
  const {
    isLoading,
    data: listResponse,
    isFetching
  } = useWatchlistListQuery({
    params: { link: link, date: withoutDateFilter ? undefined : date }
  });

  const isAlreadyAdded = listResponse && listResponse.count > 0;

  const buttonProps = {
    label: t(translations.pages.home.watchlist.heading()),
    icon: isAlreadyAdded ? (
      <BookmarkAdded sx={{ color: 'lightblue' }} />
    ) : (
      <BookmarkAdd sx={{ color: 'lightblue' }} />
    ),
    disabled: isAlreadyAdded
  };

  //hide the button while the first request is still pending
  const hideButton = isLoading || isFetching;

  if (hideButton) return <Skeleton width={120} height={30} sx={{ transform: 'none' }} />;

  if (menuButton)
    return (
      <MenuItem
        onClick={() => {
          if (date)
            // Todo Remove when in module selection date cant be null
            handleAdd({ categoryName, link, title, description, date });
        }}
      >
        {t(translations.pages.home.watchlist.heading())}
      </MenuItem>
    );

  if (gridCellMenuItem) {
    return (
      <Grid className="watchlist__cellItem">
        <GridActionsCellItem
          icon={buttonProps.icon}
          label={buttonProps.label}
          disabled={buttonProps.disabled}
          showInMenu={showInMenu}
          onClick={() => {
            if (date)
              // Todo Remove when in module selection date cant be null
              handleAdd({ categoryName, link, title, description, date });
          }}
        />
      </Grid>
    );
  }

  if (eventButton) {
    return (
      <Fab
        onClick={() => {
          if (date)
            // Todo Remove when in module selection date cant be null
            handleAdd({ categoryName, link, title, description, date });
        }}
        size="small"
        sx={theme => ({ marginX: theme.spacing(1) })}
        disabled={buttonProps.disabled}
      >
        {buttonProps.icon}
      </Fab>
    );
  }

  return (
    <Grid className={`watchlist__button--${buttonProps.disabled ? 'disabled' : 'active'}`}>
      <Button
        size={size || 'small'}
        color="info"
        variant={variant || 'contained'}
        startIcon={buttonProps.icon}
        disabled={buttonProps.disabled}
        onClick={() => {
          if (date)
            // Todo Remove when in module selection date cant be null
            handleAdd({ categoryName, link, title, description, date });
        }}
      >
        {buttonProps.label}
      </Button>
    </Grid>
  );
};
